/**
 * The fare card: what the ride came to, and the four questions a fare dispute asks of it.
 *
 * Was it what the client was told on the phone? Did the taximeter agree with the tariff? What
 * did a kilometre cost? And what did the long way round add — the kilometres over the best
 * route at the tariff's own rate per kilometre, which is the figure a "he drove me round to
 * charge me more" complaint is really about. Then the fare line by line, its figures in the
 * firm's number format, with the total under it.
 */

import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import { Card, CardHeader, Pill, Sub, cx } from '../ui';
import {
  distanceWords,
  fareFacts,
  fareLinesToShow,
  fareLineWords,
  type JourneyDetail,
} from './journey';
import { Figure } from './JourneySpeed';

export function JourneyFare({
  journey,
  className,
}: {
  journey: JourneyDetail;
  className?: string;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const facts = fareFacts(journey);
  const lines = fareLinesToShow(journey.measures.fareLines);
  const total = journey.measures.fareLines.reduce((sum, line) => sum + line.amount, 0);
  const money = (amount: number) => formats.money(amount);

  return (
    <Card className={cx('flex flex-col', className)}>
      <CardHeader title={t('reports.journeys.fare.title')}>
        {journey.paymentMethod && (
          <Pill soft tone="neutral">
            {t(`payment.method.${journey.paymentMethod}`, { defaultValue: journey.paymentMethod })}
          </Pill>
        )}
      </CardHeader>

      {facts.paid === null || facts.source === null ? (
        <p className="m-0 px-4 py-3 text-table text-ink-3">{t('reports.journeys.fare.none')}</p>
      ) : (
        <div className="min-h-0 overflow-y-auto">
          <div className="flex items-baseline gap-2 flex-wrap px-4 pt-3">
            <span className="text-page font-semibold tabular-nums">{money(facts.paid)}</span>
            <Sub>{t(`reports.journeys.fare.source.${facts.source}`)}</Sub>
          </div>

          <dl className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-4 gap-y-3 px-4 pt-2 pb-3 m-0 border-b border-rule-soft">
            {facts.quoted != null && facts.overQuote !== null && (
              <Figure
                label={t('reports.journeys.fare.quoted')}
                value={money(facts.quoted)}
                sub={
                  facts.overQuote === 0
                    ? t('reports.journeys.fare.asQuoted')
                    : facts.overQuote > 0
                      ? t('reports.journeys.fare.overQuote', { amount: money(facts.overQuote) })
                      : t('reports.journeys.fare.underQuote', { amount: money(-facts.overQuote) })
                }
              />
            )}
            {facts.computed != null && facts.meterOverComputed !== null && (
              <Figure
                label={t('reports.journeys.fare.computed')}
                value={money(facts.computed)}
                tone={facts.meterOverComputed > 0 ? 'text-warn' : undefined}
                sub={
                  facts.meterOverComputed === 0
                    ? t('reports.journeys.fare.meterAgrees')
                    : facts.meterOverComputed > 0
                      ? t('reports.journeys.fare.meterOver', {
                          amount: money(facts.meterOverComputed),
                        })
                      : t('reports.journeys.fare.meterUnder', {
                          amount: money(-facts.meterOverComputed),
                        })
                }
              />
            )}
            {facts.perKm !== null && journey.measures.rideDrivenMetres != null && (
              <Figure
                label={t('reports.journeys.fare.perKm')}
                value={t('reports.journeys.fare.perKmValue', { amount: money(facts.perKm) })}
                sub={t('reports.journeys.fare.perKmSub', {
                  distance: distanceWords(t, formats, journey.measures.rideDrivenMetres),
                })}
              />
            )}
            {facts.detourCost !== null && facts.detourMetres !== null && (
              <Figure
                label={t('reports.journeys.fare.detour')}
                value={
                  facts.detourCost > 0
                    ? t('reports.journeys.fare.detourCost', { amount: money(facts.detourCost) })
                    : t('reports.journeys.fare.detourNone')
                }
                tone={facts.detourCost > 0 ? 'text-warn' : 'text-ok'}
                sub={
                  facts.detourCost > 0
                    ? t('reports.journeys.fare.detourSub', {
                        distance: distanceWords(t, formats, facts.detourMetres),
                      })
                    : t('reports.journeys.fare.detourNoneSub')
                }
              />
            )}
          </dl>

          {lines.length > 0 && (
            <section className="px-4 pt-2.5 pb-3">
              <div className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3 mb-1.5">
                {t('reports.journeys.fare.lines')}
              </div>
              <ul className="m-0 p-0 list-none text-table">
                {lines.map((line) => (
                  <li
                    key={line.code}
                    className="flex justify-between gap-4 py-0.5 border-b border-rule-soft"
                  >
                    <span className="min-w-0">{fareLineWords(t, line, formats.decimal)}</span>
                    <span className="tabular-nums">{money(line.amount)}</span>
                  </li>
                ))}
                <li className="flex justify-between gap-4 pt-1 font-semibold">
                  <span>{t('reports.journeys.fare.total')}</span>
                  <span className="tabular-nums">{money(total)}</span>
                </li>
              </ul>
            </section>
          )}
        </div>
      )}
    </Card>
  );
}
